import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';

/**
 * The account record: staff notes and customer contacts.
 *
 * MSP-side only. Every one of these endpoints refuses a tenant-scoped caller,
 * so these hooks are only ever used from the console's customer-account screen
 * and never from /manage, which is the customer's own surface.
 */

export interface AccountNote {
  id: string;
  body: string;
  author_user_id: string | null;
  author_name: string | null;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
}

export interface AccountContact {
  id: string;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export interface AccountDocument {
  id: string;
  title: string;
  category: string | null;
  description: string | null;
  original_name: string;
  mime_type: string;
  bytes: number;
  sha256: string | null;
  uploaded_by_name: string | null;
  created_at: string;
}

export interface DocumentList {
  data: AccountDocument[];
  /** False when the deployment has no storage credentials configured. */
  storage_available: boolean;
  max_bytes: number;
  accepted: string[];
}

export const accountKeys = {
  notes: (t: string) => ['account', t, 'notes'] as const,
  contacts: (t: string) => ['account', t, 'contacts'] as const,
  documents: (t: string) => ['account', t, 'documents'] as const,
};

export const useAccountNotes = (tenantId: string) =>
  useQuery({
    queryKey: accountKeys.notes(tenantId),
    queryFn: () => api.get<{ data: AccountNote[] }>(`/v1/tenants/${tenantId}/notes`),
    enabled: !!tenantId,
  });

export const useAccountContacts = (tenantId: string) =>
  useQuery({
    queryKey: accountKeys.contacts(tenantId),
    queryFn: () => api.get<{ data: AccountContact[] }>(`/v1/tenants/${tenantId}/contacts`),
    enabled: !!tenantId,
  });

export function useSaveNote(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string; body?: string; is_pinned?: boolean }) =>
      id
        ? api.patch<AccountNote>(`/v1/tenants/${tenantId}/notes/${id}`, body)
        : api.post<AccountNote>(`/v1/tenants/${tenantId}/notes`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: accountKeys.notes(tenantId) }),
  });
}

export function useDeleteNote(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/tenants/${tenantId}/notes/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: accountKeys.notes(tenantId) }),
  });
}

export function useSaveContact(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id
        ? api.patch<AccountContact>(`/v1/tenants/${tenantId}/contacts/${id}`, body)
        : api.post<AccountContact>(`/v1/tenants/${tenantId}/contacts`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: accountKeys.contacts(tenantId) }),
  });
}

export function useDeleteContact(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/tenants/${tenantId}/contacts/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: accountKeys.contacts(tenantId) }),
  });
}

export const useAccountDocuments = (tenantId: string) =>
  useQuery({
    queryKey: accountKeys.documents(tenantId),
    queryFn: () => api.get<DocumentList>(`/v1/tenants/${tenantId}/documents`),
    enabled: !!tenantId,
  });

export function useDeleteDocument(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/tenants/${tenantId}/documents/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: accountKeys.documents(tenantId) }),
  });
}
